// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { Button, TextField } from "@granite/ui";
import { useMutation } from "@tanstack/react-query";
import { getRouteApi, Link, useRouter } from "@tanstack/react-router";
import { useState, type SubmitEvent } from "react";

import { AuthLayout, FormAlert, linkClass } from "../components/AuthLayout.js";
import { OtherSignIn } from "../components/OtherSignIn.js";
import { errorCode, errorMessage, safeRedirect } from "../lib/errors.js";
import { t } from "../lib/i18n.js";
import { isDesktop } from "../lib/platform.js";
import { isFullOtp, otpDigits } from "../lib/otp.js";
import { login, loginSecondFactor, type MFAChallenge } from "../lib/session.js";

const route = getRouteApi("/login");

export function LoginPage() {
  const { redirect } = route.useSearch();
  const router = useRouter();
  const [challenge, setChallenge] = useState<MFAChallenge>();
  const [restartError, setRestartError] = useState<string>();

  const done = () => {
    router.history.push(safeRedirect(redirect));
  };

  if (challenge) {
    return (
      <SecondFactorForm
        challenge={challenge}
        onDone={done}
        onRestart={(message) => {
          setChallenge(undefined);
          setRestartError(message);
        }}
      />
    );
  }
  return (
    <PasswordForm
      next={safeRedirect(redirect)}
      initialError={restartError}
      onDone={done}
      onChallenge={(c) => {
        setRestartError(undefined);
        setChallenge(c);
      }}
    />
  );
}

function PasswordForm({
  next,
  initialError,
  onDone,
  onChallenge,
}: {
  next: string;
  initialError: string | undefined;
  onDone: () => void;
  onChallenge: (c: MFAChallenge) => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: () => login(email.trim(), password),
    onSuccess: (result) => {
      if (result.kind === "mfa") onChallenge(result.challenge);
      else onDone();
    },
  });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  const error = mutation.error ? errorMessage(mutation.error) : initialError;
  return (
    <AuthLayout
      title={t("login.title")}
      footer={
        <>
          {t("login.noAccount")}{" "}
          <Link to="/signup" className={linkClass}>
            {t("login.createAccount")}
          </Link>
        </>
      }
    >
      {error && (
        <FormAlert tone="error">
          {error}
          {errorCode(mutation.error) === "sso_required" && (
            <>
              {" "}
              <Link to="/sso" search={{ email: email.trim() }} className={linkClass}>
                {t("sso.title")}
              </Link>
            </>
          )}
          {errorCode(mutation.error) === "email_not_verified" && (
            <>
              {" "}
              <Link to="/check-email" search={{ email: email.trim() }} className={linkClass}>
                {t("login.resendVerification")}
              </Link>
            </>
          )}
        </FormAlert>
      )}
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <TextField
          label={t("common.email")}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
        <TextField
          label={t("common.password")}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
        <Button type="submit" loading={mutation.isPending}>
          {t("login.submit")}
        </Button>
        <Link to="/reset-password" className={`${linkClass} self-center text-sm`}>
          {t("login.forgot")}
        </Link>
        {isDesktop && (
          <Link to="/local" className={`${linkClass} self-center text-sm`}>
            {t("login.withoutAccount")}
          </Link>
        )}
      </form>
      <OtherSignIn next={next} ssoEmail={email.trim()} />
    </AuthLayout>
  );
}

export function SecondFactorForm({
  challenge,
  onDone,
  onRestart,
}: {
  challenge: MFAChallenge;
  onDone: () => void;
  onRestart: (message?: string) => void;
}) {
  const canUseRecovery = challenge.methods.includes("recovery_code");
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState("");
  const mutation = useMutation({
    mutationFn: (typed: string) =>
      loginSecondFactor(challenge.mfa_token, useRecovery ? { recovery_code: typed.trim() } : { code: otpDigits(typed) }),
    onSuccess: onDone,
    onError: (err) => {
      if (errorCode(err) === "invalid_mfa_token") onRestart(errorMessage(err));
    },
  });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    mutation.mutate(value);
  };

  return (
    <AuthLayout title={t("mfa.title")}>
      {mutation.error && <FormAlert tone="error">{errorMessage(mutation.error)}</FormAlert>}
      <p className="mb-4 text-sm text-fg-muted">{useRecovery ? t("mfa.introRecovery") : t("mfa.intro")}</p>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {useRecovery ? (
          <TextField
            key="recovery"
            label={t("mfa.recoveryCode")}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            required
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
          />
        ) : (
          <TextField
            key="code"
            label={t("mfa.code")}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            required
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              // The sixth digit (typed or pasted) signs in; no need to press the button.
              if (isFullOtp(e.target.value) && !mutation.isPending) mutation.mutate(e.target.value);
            }}
          />
        )}
        <Button type="submit" loading={mutation.isPending}>
          {t("mfa.submit")}
        </Button>
        <div className="flex flex-col items-start gap-2 text-sm">
          {canUseRecovery && (
            <button
              type="button"
              className={linkClass}
              onClick={() => {
                setUseRecovery(!useRecovery);
                setValue("");
                mutation.reset();
              }}
            >
              {useRecovery ? t("mfa.useApp") : t("mfa.useRecovery")}
            </button>
          )}
          <button
            type="button"
            className={linkClass}
            onClick={() => {
              onRestart();
            }}
          >
            {t("mfa.startOver")}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
